import { useState } from "react";
import { Clock, Languages } from "lucide-react";

import { toast } from "utils/toast";
import { detectTextLang, translations } from "utils/i18n";
import {
  EXPORT_FORMATS,
  FORMAT_EXTENSIONS,
  getLanguageNames,
  renderTranscript,
  TIMED_FORMATS,
  TRANSLATION_LANGUAGES,
} from "utils/transcript";
import { cn } from "lib/utils";
import { useLanguage } from "context/languageContext";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";

// Soniox-style include chip: on, off, and clearly both. A plain <button>, so
// it needs data-slot="button" to opt out of app.css's legacy solid-fill
// button style (see "the white box problem") and its own outline reset,
// since it doesn't get any of that for free the way the shadcn Button does.
function IncludeChip({ icon: Icon, label, active, disabled, onClick }) {
  return (
    <button
      type="button"
      data-slot="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex appearance-none items-center gap-2 rounded-lg border px-3 py-2 text-sm outline-none transition-colors",
        "focus-visible:ring-1 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-60",
        active
          ? "border-foreground/30 bg-accent text-foreground"
          : "border-border bg-transparent text-muted-foreground hover:bg-accent"
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

/**
 * Step two of the export. The format arrives preselected from the header's
 * dropdown and can still be changed here; the preview is the exact text
 * that gets written out.
 */
export function ExportDialog({ open, onOpenChange, fileName, languageCode, lines, translations: translationsCache, initialFormat }) {
  const { language } = useLanguage();
  const tr = translations[language].exportDialog;
  const languageNames = getLanguageNames(language);
  const [format, setFormat] = useState(initialFormat);
  const [timestamps, setTimestamps] = useState(true);
  const [languageLabels, setLanguageLabels] = useState(false);
  const [labelStyle, setLabelStyle] = useState("name");

  // Every language with content actually loaded this session — same
  // ordering as the Translation dropdown — plus the transcript's own
  // original, first. Anything translated but not currently loaded (see the
  // editor page's `translations` cache) just isn't offered here; there's
  // nothing to export for it yet.
  const availableLangs = [
    languageCode,
    ...TRANSLATION_LANGUAGES.filter((code) => code !== languageCode && translationsCache?.[code]),
  ];
  const [selectedLangs, setSelectedLangs] = useState(availableLangs);

  // Each opening follows the format picked in the dropdown, and starts with
  // every available language selected.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setFormat(initialFormat);
      setSelectedLangs(availableLangs);
    }
  }

  function toggleLang(code) {
    setSelectedLangs((prev) => {
      if (prev.includes(code)) {
        if (prev.length === 1) return prev; // always keep at least one
        return prev.filter((c) => c !== code);
      }
      // Keep dropdown/preview ordering stable regardless of click order.
      return availableLangs.filter((c) => prev.includes(c) || c === code);
    });
  }

  const timingIsFixed = TIMED_FORMATS.includes(format);
  const multiLang = selectedLangs.length > 1;
  const options = {
    timestamps: timingIsFixed ? true : timestamps,
    languageLabels,
    languageLabelStyle: labelStyle,
    uiLang: language,
  };
  const preview = renderTranscript(lines, languageCode, translationsCache, selectedLangs, format, options);

  function download() {
    const blob = new Blob([preview], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fullFileName = `${fileName}.${FORMAT_EXTENSIONS[format]}`;
    link.download = fullFileName;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    onOpenChange(false);
    toast.success(tr.exportedToast(fullFileName));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{tr.title}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm font-medium">{tr.preview}</p>
            <select
              value={format}
              onChange={(event) => setFormat(event.target.value)}
              aria-label={tr.formatSelectLabel}
              className="h-8 w-28 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {EXPORT_FORMATS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <pre
            lang={detectTextLang(preview)}
            className="h-56 overflow-auto rounded-lg border border-border bg-muted/50 p-4 text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground"
          >
            {preview}
          </pre>

          {availableLangs.length > 1 ? (
            <div className="grid gap-3">
              <p className="text-sm font-medium">{tr.languagesHeading}</p>
              <div className="flex flex-wrap gap-2">
                {availableLangs.map((code) => (
                  <IncludeChip
                    key={code}
                    icon={Languages}
                    label={languageNames[code] ?? code.toUpperCase()}
                    active={selectedLangs.includes(code)}
                    onClick={() => toggleLang(code)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid gap-3">
            <p className="text-sm font-medium">{tr.includeHeading}</p>
            <div className="flex flex-wrap gap-2">
              <IncludeChip
                icon={Clock}
                label={tr.timestamps}
                active={options.timestamps}
                disabled={timingIsFixed}
                onClick={() => setTimestamps((on) => !on)}
              />
              <IncludeChip
                icon={Languages}
                label={tr.languageLabels}
                active={multiLang || languageLabels}
                disabled={multiLang}
                onClick={() => setLanguageLabels((on) => !on)}
              />
            </div>
            {timingIsFixed ? (
              <p className="text-xs text-muted-foreground">{tr.fixedTimestampsHint(format)}</p>
            ) : null}
            {multiLang ? <p className="text-xs text-muted-foreground">{tr.multiLangHint}</p> : null}

            {languageLabels || multiLang ? (
              <div className="flex flex-wrap gap-2 ps-1">
                {[
                  ["name", tr.languageStyleName],
                  ["code", tr.languageStyleCode],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-slot="button"
                    onClick={() => setLabelStyle(value)}
                    aria-pressed={labelStyle === value}
                    className={cn(
                      "appearance-none rounded-md border bg-transparent px-2.5 py-1.5 text-xs outline-none transition-colors",
                      "focus-visible:ring-1 focus-visible:ring-ring",
                      labelStyle === value
                        ? "border-foreground/30 bg-accent text-foreground"
                        : "border-border text-muted-foreground hover:bg-accent"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {tr.cancel}
          </Button>
          <Button onClick={download}>{tr.download(format)}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
